/**
 * Salesforce Sync Vuex Module
 *
 * Manages state for bulk Salesforce opportunity amount syncing operations.
 * Handles API calls to SalesforceAmountSyncController endpoints and manages
 * polling intervals for progress tracking.
 *
 * @see docs/plans/2025-12-30-salesforce-donation-amount-sync.md
 */

// Default polling interval in milliseconds
const POLLING_INTERVAL_MS = 2000;

// Local storage key for persisting session across page refreshes
const STORAGE_KEY = 'activeSyncSession';

/**
 * Initial state factory
 */
const state = () => ({
    // Whether a sync operation is currently running
    isRunning: false,

    // The active session ID from the backend
    sessionId: null,

    // Progress data from the server
    progress: {
        total: 0,
        processed: 0,
        successful: 0,
        failed: 0,
        status: null, // 'running', 'completed', 'failed', 'cancelled'
        current_operation: '',
        progress_percent: 0,
        eta_seconds: 0,
        eta_readable: '',
        started_at: null,
    },

    // Error state
    error: null,

    // Last completed session for reference
    lastCompletedSession: null,

    // Internal polling reference (not persisted)
    _pollIntervalId: null,
});

/**
 * Mutations for state updates
 */
const mutations = {
    /**
     * Set the running state
     */
    SET_RUNNING(state, isRunning) {
        state.isRunning = isRunning;
    },

    /**
     * Set the active session ID
     */
    SET_SESSION(state, sessionId) {
        state.sessionId = sessionId;
        if (sessionId) {
            localStorage.setItem(STORAGE_KEY, sessionId);
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    },

    /**
     * Update progress data from server response
     */
    SET_PROGRESS(state, progress) {
        state.progress = {
            total: progress.total || 0,
            processed: progress.processed || 0,
            successful: progress.successful || 0,
            failed: progress.failed || 0,
            status: progress.status || null,
            current_operation: progress.current_operation || '',
            progress_percent: progress.progress_percent || 0,
            eta_seconds: progress.eta_seconds || 0,
            eta_readable: progress.eta_readable || '',
            started_at: progress.started_at || null,
        };
    },

    /**
     * Set error state
     */
    SET_ERROR(state, error) {
        state.error = error;
    },

    /**
     * Clear the current session
     */
    CLEAR_SESSION(state) {
        state.sessionId = null;
        state.isRunning = false;
        state.progress = {
            total: 0,
            processed: 0,
            successful: 0,
            failed: 0,
            status: null,
            current_operation: '',
            progress_percent: 0,
            eta_seconds: 0,
            eta_readable: '',
            started_at: null,
        };
        state.error = null;
        localStorage.removeItem(STORAGE_KEY);
    },

    /**
     * Store last completed session for reference
     */
    SET_LAST_COMPLETED(state, session) {
        state.lastCompletedSession = session;
    },

    /**
     * Store the polling interval ID (internal use)
     */
    _SET_POLL_INTERVAL(state, intervalId) {
        state._pollIntervalId = intervalId;
    },
};

/**
 * Getters for computed state values
 */
const getters = {
    /**
     * Progress as a percentage (0-100)
     */
    progressPercent(state) {
        if (state.progress.total === 0) return 0;
        return Math.round((state.progress.processed / state.progress.total) * 100);
    },

    /**
     * Human-readable ETA string
     */
    etaReadable(state) {
        if (!state.isRunning || state.progress.eta_seconds <= 0) {
            return '';
        }
        return state.progress.eta_readable || formatEtaSeconds(state.progress.eta_seconds);
    },

    /**
     * Status message based on current state
     */
    statusMessage(state) {
        if (state.error) {
            return `Error: ${state.error}`;
        }

        if (!state.isRunning && !state.sessionId) {
            return 'Ready to sync';
        }

        switch (state.progress.status) {
            case 'running':
                return state.progress.current_operation || 'Syncing...';
            case 'completed':
                return `Completed: ${state.progress.successful} successful, ${state.progress.failed} failed`;
            case 'failed':
                return 'Sync failed';
            case 'cancelled':
                return 'Sync cancelled';
            default:
                return 'Initializing...';
        }
    },

    /**
     * Whether there is an error
     */
    hasError(state) {
        return state.error !== null;
    },

    /**
     * Whether the sync is complete (success or failure)
     */
    isComplete(state) {
        return ['completed', 'failed', 'cancelled'].includes(state.progress.status);
    },

    /**
     * Get active session from localStorage (for persistence across page refreshes)
     */
    persistedSessionId() {
        return localStorage.getItem(STORAGE_KEY);
    },
};

/**
 * Actions for async operations
 */
const actions = {
    /**
     * Start a bulk sync operation
     *
     * @param {Object} context - Vuex context
     * @param {Object} options - Sync options
     * @param {string} options.syncType - Type of sync ('all_campaigns', 'mismatched_only', etc.)
     * @returns {Promise<string>} - Session ID
     */
    async startBulkSync({ commit, dispatch }, options = {}) {
        commit('SET_ERROR', null);
        commit('SET_RUNNING', true);

        try {
            const response = await window.axios.post('/admin/salesforce/amount-sync/bulk-sync/start', {
                sync_type: options.syncType || 'all_campaigns',
            });

            const sessionId = response.data.session_id;
            commit('SET_SESSION', sessionId);

            // Start polling for progress
            dispatch('pollProgress');

            return sessionId;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to start sync';
            commit('SET_ERROR', errorMessage);
            commit('SET_RUNNING', false);
            throw error;
        }
    },

    /**
     * Poll for sync progress
     * Sets up an interval to fetch progress every POLLING_INTERVAL_MS
     *
     * @param {Object} context - Vuex context
     */
    pollProgress({ state, commit, dispatch }) {
        // Stop any existing polling first
        dispatch('stopPolling');

        if (!state.sessionId) {
            return;
        }

        // Fetch immediately, then set up interval
        dispatch('fetchProgress');

        const intervalId = setInterval(() => {
            dispatch('fetchProgress');
        }, POLLING_INTERVAL_MS);

        commit('_SET_POLL_INTERVAL', intervalId);
    },

    /**
     * Fetch current progress from the server
     *
     * @param {Object} context - Vuex context
     */
    async fetchProgress({ state, commit, dispatch }) {
        if (!state.sessionId) {
            return;
        }

        try {
            const response = await window.axios.get(
                `/admin/salesforce/amount-sync/bulk-sync/progress/${state.sessionId}`
            );

            const progressData = response.data.data;
            commit('SET_PROGRESS', progressData);

            // Check if sync is complete
            if (['completed', 'failed', 'cancelled'].includes(progressData.status)) {
                dispatch('stopPolling');
                commit('SET_RUNNING', false);
                commit('SET_LAST_COMPLETED', {
                    sessionId: state.sessionId,
                    ...progressData,
                    completedAt: new Date().toISOString(),
                });
            }
        } catch (error) {
            console.error('Failed to fetch sync progress:', error);

            // If we get a 404, the session doesn't exist anymore
            if (error.response?.status === 404) {
                dispatch('stopPolling');
                commit('CLEAR_SESSION');
            } else {
                commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch progress');
            }
        }
    },

    /**
     * Stop polling for progress
     *
     * @param {Object} context - Vuex context
     */
    stopPolling({ state, commit }) {
        if (state._pollIntervalId) {
            clearInterval(state._pollIntervalId);
            commit('_SET_POLL_INTERVAL', null);
        }
    },

    /**
     * Cancel the current sync operation
     *
     * @param {Object} context - Vuex context
     * @returns {Promise<void>}
     */
    async cancelSync({ state, commit, dispatch }) {
        if (!state.sessionId) {
            return;
        }

        try {
            await window.axios.post(
                `/admin/salesforce/amount-sync/bulk-sync/cancel/${state.sessionId}`
            );

            dispatch('stopPolling');
            commit('SET_PROGRESS', { ...state.progress, status: 'cancelled' });
            commit('SET_RUNNING', false);
        } catch (error) {
            console.error('Failed to cancel sync:', error);
            commit('SET_ERROR', error.response?.data?.message || 'Failed to cancel sync');
        }
    },

    /**
     * Dismiss/clear the current session
     * Used when user manually closes the progress banner
     *
     * @param {Object} context - Vuex context
     */
    dismissSession({ commit, dispatch }) {
        dispatch('stopPolling');
        commit('CLEAR_SESSION');
    },

    /**
     * Restore session from localStorage (for page refresh recovery)
     *
     * @param {Object} context - Vuex context
     */
    async restoreSession({ commit, dispatch, getters }) {
        const sessionId = getters.persistedSessionId;

        if (sessionId) {
            commit('SET_SESSION', sessionId);
            commit('SET_RUNNING', true);

            // Try to fetch progress to see if session is still valid
            try {
                const response = await window.axios.get(
                    `/admin/salesforce/amount-sync/bulk-sync/progress/${sessionId}`
                );

                const progressData = response.data.data;
                commit('SET_PROGRESS', progressData);

                // If still running, start polling
                if (progressData.status === 'running') {
                    dispatch('pollProgress');
                } else {
                    commit('SET_RUNNING', false);
                }
            } catch (error) {
                // Session no longer exists
                commit('CLEAR_SESSION');
            }
        }
    },
};

/**
 * Helper function to format seconds into human-readable ETA
 *
 * @param {number} seconds - Number of seconds
 * @returns {string} - Formatted string
 */
function formatEtaSeconds(seconds) {
    if (seconds < 60) {
        return `${seconds} seconds`;
    } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes} min ${secs} sec`;
    } else {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours} hr ${minutes} min`;
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};
